import React, { useState, useEffect } from 'react'
import { X, RefreshCw, Download, Heart, MessageCircle, Share2, Eye, User } from 'lucide-react'
import { getPublicImages } from '../../lib/supabase'
import CommentSection from '../Comments/CommentSection'
import { useDeviceType } from '../../hooks/useDeviceType'

interface PublicImage {
  id: string
  title: string
  description?: string
  prompt_text: string
  image_url: string
  is_public: boolean
  is_nsfw: boolean
  likes_count: number
  views_count: number
  created_at: string
  profiles?: {
    username?: string
    display_name?: string
  }
}

interface DiscoverFeedProps {
  isOpen: boolean
  onClose: () => void
}

const DiscoverFeed: React.FC<DiscoverFeedProps> = ({ isOpen, onClose }) => {
  const { isMobile } = useDeviceType()
  const [images, setImages] = useState<PublicImage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<PublicImage | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadPublicImages()
    }
  }, [isOpen])

  const loadPublicImages = async () => {
    try {
      setLoading(true)
      const publicImages = await getPublicImages(50) // Load more for mosaic
      setImages(publicImages as PublicImage[])
    } catch (error) {
      console.error('Error loading public images:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadPublicImages()
    setRefreshing(false)
  }

  const handleImageClick = (image: PublicImage) => {
    setSelectedImage(image)
  }

  const handleDownload = async (image: PublicImage) => {
    try {
      const link = document.createElement('a')
      link.href = image.image_url
      link.download = `toefetti_${image.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading image:', error)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  // Mosaic layout for desktop
  const MosaicLayout = () => {
    if (images.length === 0) return null

    // Create a grid with varying sizes
    const getMosaicClass = (index: number) => {
      const patterns = [
        'col-span-2 row-span-2', // Large square
        'col-span-1 row-span-1', // Small square
        'col-span-1 row-span-1', // Small square
        'col-span-1 row-span-2', // Tall rectangle
        'col-span-2 row-span-1', // Wide rectangle
        'col-span-1 row-span-1', // Small square
        'col-span-1 row-span-1', // Small square
        'col-span-1 row-span-1', // Small square
      ]
      return patterns[index % patterns.length]
    }

    return (
      <div className="grid grid-cols-4 gap-2 auto-rows-[200px] p-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            className={`${getMosaicClass(index)} relative group cursor-pointer overflow-hidden rounded-lg bg-gray-100`}
            onClick={() => handleImageClick(image)}
          >
            <img
              src={image.image_url}
              alt={image.title}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
            />
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white text-center">
                <div className="flex items-center justify-center space-x-4 mb-2">
                  <div className="flex items-center space-x-1">
                    <Heart className="w-4 h-4" />
                    <span className="text-sm">{image.likes_count}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">{image.views_count}</span>
                  </div>
                </div>
                <p className="text-sm font-medium truncate px-2">{image.title}</p>
              </div>
            </div>

            {/* User avatar in corner */}
            <div className="absolute top-2 left-2 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-sm">
              <User className="w-4 h-4 text-gray-600" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Instagram-style feed for mobile
  const InstagramLayout = () => (
    <div className="space-y-6">
      {images.map((image) => (
        <div key={image.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="flex items-center space-x-3 p-4">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {image.profiles?.display_name?.[0] || image.profiles?.username?.[0] || '?'}
              </span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-sm">
                {image.profiles?.display_name || image.profiles?.username || 'Anonymous'}
              </h3>
              <p className="text-xs text-gray-500">{formatDate(image.created_at)}</p>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src={image.image_url}
              alt={image.title}
              className="w-full aspect-square object-cover cursor-pointer"
              onClick={() => handleImageClick(image)}
            />
          </div>

          {/* Actions */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4">
                <button className="flex items-center space-x-1 text-gray-600 hover:text-red-500">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm">{image.likes_count}</span>
                </button>
                <button 
                  className="flex items-center space-x-1 text-gray-600 hover:text-blue-500"
                  onClick={() => handleImageClick(image)}
                >
                  <MessageCircle className="w-5 h-5" />
                </button>
                <button className="text-gray-600 hover:text-gray-800">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              <button 
                onClick={() => handleDownload(image)}
                className="text-gray-600 hover:text-gray-800"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>

            {/* Title and description */}
            <div className="space-y-1">
              <h4 className="font-semibold text-gray-900">{image.title}</h4>
              {image.description && (
                <p className="text-gray-600 text-sm">{image.description}</p>
              )}
              <p className="text-gray-500 text-xs">"{image.prompt_text}"</p>
            </div>

            {/* View counts */}
            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
              <span>{image.views_count} views</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Discover</h2>
              <p className="text-sm text-gray-600">Explore public creations from the community</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading amazing foot art...</p>
              </div>
            </div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No public images yet</h3>
                <p className="text-gray-600">Be the first to share your creations with the community!</p>
              </div>
            </div>
          ) : (
            <>
              {isMobile ? <InstagramLayout /> : <MosaicLayout />}
            </>
          )}
        </div>
      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-2xl w-full h-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {selectedImage.profiles?.display_name?.[0] || selectedImage.profiles?.username?.[0] || '?'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedImage.profiles?.display_name || selectedImage.profiles?.username || 'Anonymous'}
                  </h3>
                  <p className="text-sm text-gray-500">{formatDate(selectedImage.created_at)}</p>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedImage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className={`${isMobile ? 'block' : 'grid grid-cols-2'} h-full`}>
                {/* Image */}
                <div className="flex items-center justify-center bg-gray-50 p-4">
                  <img
                    src={selectedImage.image_url}
                    alt={selectedImage.title}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>

                {/* Details and Comments */}
                <div className="flex flex-col h-full">
                  {/* Image Info */}
                  <div className="p-4 border-b border-gray-200">
                    <h4 className="font-bold text-lg text-gray-900 mb-2">{selectedImage.title}</h4>
                    {selectedImage.description && (
                      <p className="text-gray-600 mb-3">{selectedImage.description}</p>
                    )}
                    <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg mb-3">
                      "{selectedImage.prompt_text}"
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Heart className="w-4 h-4" />
                        <span>{selectedImage.likes_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{selectedImage.views_count}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 mt-4">
                      <button
                        onClick={() => handleDownload(selectedImage)}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                        <Share2 className="w-4 h-4" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="flex-1 p-4 overflow-y-auto">
                    <CommentSection 
                      imageId={selectedImage.id} 
                      isPublic={selectedImage.is_public} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiscoverFeed 